import { useQuery } from '@tanstack/react-query';
import { ORPCError } from '@orpc/client';
import { orpc } from '@/api/rpc/client';
import type { RexelFileType } from '@erp/rpc';

export type { RexelFileType };

export interface RexelFileSearchParams {
  fileType: RexelFileType;
  source?: 'azure' | 'database';
  company?: string;
  division?: string;
  branch?: string;
  tradingPartner?: string;
  invoiceNumber?: string;
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  location?: string;
  supplierNumber?: string;
  itemNumber?: string;
  vend?: string;
  fileDate?: string;
  filters?: Record<string, string>;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

function hasActiveFilters(params: RexelFileSearchParams): boolean {
  const hasBasic = !!(
    params.company ||
    params.division ||
    params.branch ||
    params.tradingPartner ||
    params.invoiceNumber ||
    (params.fileType === 'e844sd' &&
      (params.invoiceDateFrom || params.invoiceDateTo)) ||
    params.location ||
    params.supplierNumber ||
    params.itemNumber ||
    params.vend
  );
  const hasDynamic = params.filters
    ? Object.values(params.filters).some((v) => !!v)
    : false;
  return hasBasic || hasDynamic;
}

function isNotFoundError(error: unknown): boolean {
  return error instanceof ORPCError && error.code === 'NOT_FOUND';
}

export function useRexelFileSearch(
  params: RexelFileSearchParams,
  enabled = true,
) {
  const isEnabled = enabled && hasActiveFilters(params);
  const query = useQuery({
    ...orpc.shipAndDebit.rexelFileSearch.search.queryOptions({
      input: params,
    }),
    enabled: isEnabled,
    staleTime: 0,
    gcTime: 0,
    retry: (failureCount, error) => {
      if (isNotFoundError(error)) return false;
      return failureCount < 3;
    },
  });

  const notFound = isNotFoundError(query.error);
  const notFoundMessage = notFound
    ? (query.error as ORPCError<string, unknown>).message
    : undefined;

  return {
    ...query,
    transactions: isEnabled ? (query.data?.transactions ?? []) : [],
    pagination: isEnabled ? query.data?.pagination : undefined,
    fileMetadata: isEnabled ? (query.data?.fileMetadata ?? []) : [],
    isNotFound: notFound,
    notFoundMessage,
  };
}
